import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

declare global {
    interface Window {
        appCacheBuster: string | null | undefined;
    }
}

// All we care about is that the subscription information we get from Taiga
// contains a plan name and plan code for each subscription. (Plus state so
// we can confirm the subscription is active.)
interface HasPlanVals {
    planCode: string;
    planName: string;
    state: string;
}
type TaigaSubscription = HasPlanVals & { [key: string]: any };
// Currently some very loose typing on BaseProduct
interface BaseProduct {
    [key: string]: boolean;
}

/**
 * A custom Lit element that renders a panel with tabs for profile and subscription
 * information for the logged-in user. This is intended to be wrapped within a "known"
 * element within a phoenix-known-user component.
 */

@customElement('phoenix-account-menu')
export default class PhoenixAccountMenu extends LitElement {
    @property({
        attribute: 'self-service-portal-url',
        type: String,
    })
    selfServicePortalUrl: string | undefined;

    @property({
        attribute: 'profile-loaded-event',
        type: String,
    })
    profileLoadedEvent: string | undefined;

    @property({
        attribute: 'logout-event',
        type: String,
    })
    logoutEvent: string | undefined;

    @state()
    private subscriptions: TaigaSubscription[] = [];

    // FIXME: Don't use shadow DOM for now, it stops event handling in the
    // (non-Lit) tab components from working properly
    protected createRenderRoot() {
        return this;
    }

    constructor() {
        super();

        const event = this.getAttribute('profile-loaded-event');
        this.profileLoadedEvent = event ? event : undefined;
        if (this.profileLoadedEvent) {
            window.addEventListener(this.profileLoadedEvent, this.profileUpdated.bind(this));
        }
    }

    connectedCallback() {
        super.connectedCallback();
    }

    profileUpdated(evt: any) {
        // There was a shift in the shape of the event detail from the profile updated event,
        // so we'll be loose in what we accept. The second, with `subscription` a direct
        // property of `knownUser`, seems to be the current one.
        const update =
            evt.detail?.knownUser?.userProfile?.subscriptions || evt.detail?.knownUser?.subscriptions;
        // Update our property with all `active` subscriptions.
        if (update) {
            this.subscriptions = update.filter((sub: TaigaSubscription) => {
                return sub.state === 'active';
            });
        }
    }

    updated() {
        if (this.logoutEvent) {
            this.getElementsByClassName('m-account-menu--logout')[0].addEventListener('click', () => {
                window.dispatchEvent(new window.CustomEvent(this.logoutEvent as string, { bubbles: true }));
            });
        }
    }

    renderSubscription(sub: any, open: boolean) {
        // All subs have names, but we may or may not have a link.
        const icon = open ? 'ico_subscription_checkmark' : 'ico_closed_lock';
        const linkClass = 'm-account-menu--subscription-link';
        const subValue = sub.url
            ? html`<a class="${linkClass}" target="_self" href="${sub.url}">${sub.name}</a>`
            : sub.name;
        const cb = window.appCacheBuster || '';
        // Note that we want to use `href`, not `xml:href`, in the `use` tag --
        // dynamically rendered HTML only renders href attributes.
        return html`
            <li class="m-account-menu--subscriptions-list-item">
                <div class="m-account-menu--icon-container">
                    <phoenix-icon-fallback class="m-svg" icon="${icon}">
                        <svg>
                            <use href="/assets-global/icons/svg-sprite.svg?${cb}#${icon}"></use>
                        </svg>
                    </phoenix-icon-fallback>
                </div>
                ${subValue}
            </li>
        `;
    }

    render() {
        // We'll check for the presence of extended subscription info to handle subscription bundles.
        // If extended subscriptions are not provided, we don't need to go through this process and
        // can provide a simpler subscription list.
        const renderOpenSubscription = (sub: any) => {
            return this.renderSubscription(sub, true);
        };
        const renderClosedSubscription = (sub: any) => {
            return this.renderSubscription(sub, false);
        };
        let extendedSubscriptionInfo: any = document.querySelector(
            '.account-menu-extended-subscription-info'
        );
        let subscriptionList, subscriptionOfferList, activeSubscriptionsHeader, showOther;
        if (extendedSubscriptionInfo) {
            try {
                extendedSubscriptionInfo = JSON.parse(extendedSubscriptionInfo.textContent);
            } catch (e) {
                extendedSubscriptionInfo = undefined;
            }
        }
        if (extendedSubscriptionInfo) {
            // We want to go through our list of subscriptions and build out a list of base subscriptions
            // (expanding bundled products), then build a list of base subscriptions.
            const baseProducts: BaseProduct = {};
            this.subscriptions?.forEach((sub: any) => {
                const planParts: string = sub.planCode && sub.planCode.split('-');
                if (planParts && planParts.length > 1) {
                    const shortCode: string = planParts[1].toUpperCase();
                    if (extendedSubscriptionInfo.baseProducts[shortCode]) {
                        baseProducts[shortCode] = true;
                    } else if (extendedSubscriptionInfo.bundleProducts[shortCode]) {
                        extendedSubscriptionInfo.bundleProducts[shortCode].forEach((code: string) => {
                            baseProducts[code] = true;
                        });
                    }
                }
            });
            subscriptionList = Object.keys(baseProducts)
                .filter((code: string) => !!extendedSubscriptionInfo.baseProducts[code])
                .map((code: string) => ({
                    name: extendedSubscriptionInfo.baseProducts[code].name,
                    url: extendedSubscriptionInfo.baseProducts[code].url
                        ? extendedSubscriptionInfo.baseProducts[code].url
                        : false,
                }));
            // Now, the offer list is all items in base products that are not in the subscription
            // list, using the "landingPage" URL instead of "url".
            subscriptionOfferList = Object.keys(extendedSubscriptionInfo.baseProducts)
                .filter((code: string) => !Object.keys(baseProducts).includes(code))
                .map((code: string) => ({
                    name: extendedSubscriptionInfo.baseProducts[code].name,
                    url: extendedSubscriptionInfo.baseProducts[code].landingPage
                        ? extendedSubscriptionInfo.baseProducts[code].landingPage
                        : false,
                }));
            activeSubscriptionsHeader = html`<li class="m-account-menu--subscriptions-header">
                Active Subscriptions (${subscriptionList.length})
            </li>`;
            showOther = false;
        } else {
            // The simpler path in our logic -- just build a list of names fom
            // the subscriptions we got from Taiga. No subscriptionOfferList
            // is required, no URLs will be displayed.
            if (this.subscriptions) {
                subscriptionList = this.subscriptions.map((sub: any) => ({
                    name: sub.planName,
                }));
            }
            activeSubscriptionsHeader = '';
            showOther = true;
        }
        /* eslint-disable max-len */
        return html`
            <phoenix-tabs class="m-tabs" id="account-menu">
                <div class="m-account-menu--tab-container">
                    <phoenix-tab for="account" aria-selected="true" class="m-account-menu--tab">
                        My Account
                    </phoenix-tab>
                    <phoenix-tab for="subscriptions" class="m-account-menu--tab">Subscriptions</phoenix-tab>
                </div>
                <phoenix-tab-panel id="account" class="m-account-menu--panel">
                    <ul class="m-account-menu--options-account">
                        <li><a href="${this.selfServicePortalUrl}#profile">Profile</a></li>
                        <li><a href="${this.selfServicePortalUrl}#email">Email and Newsletters</a></li>
                        <li><a href="${this.selfServicePortalUrl}#contact">Customer Support</a></li>
                        <li><a class="m-account-menu--logout">Sign Out</a></li>
                    </ul>
                </phoenix-tab-panel>
                <phoenix-tab-panel hidden id="subscriptions" class="m-account-menu--panel">
                    <ul class="m-account-menu--options-subscriptions">
                        ${activeSubscriptionsHeader}
                        ${Array.isArray(subscriptionList) && subscriptionList.length > 0
                            ? html`${subscriptionList?.map(renderOpenSubscription)}
                              ${showOther
                                  ? html`<li>
                                        <a href="${this.selfServicePortalUrl}#subscriptions"
                                            >See All Subscriptions</a
                                        >
                                    </li>`
                                  : ``}`
                            : html`<li>You have no active subscriptions</li>`}
                    </ul>
                    ${Array.isArray(subscriptionOfferList) && subscriptionOfferList.length > 0
                        ? html`<hr />
                              <ul class="m-account-menu--options-subscriptions">
                                  <li class="m-account-menu--subscriptions-header">Other Premium Products</li>
                                  ${subscriptionOfferList?.map(renderClosedSubscription)}
                              </ul>`
                        : html``}
                </phoenix-tab-panel>
            </phoenix-tabs>
        `;
        /* eslint-enable max-len */
    }
}
